import axios from 'axios';
import { CalendarDays, Download, Mail } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import ArrowLink from '@/Components/Marketing/ArrowLink';
import Button from '@/Components/Marketing/Button';
import CalendarPreview from '@/Components/Marketing/CalendarPreview';
import formatDay from '@/Components/Marketing/formatDay';
import LazyBoundary from '@/Components/Marketing/LazyBoundary';
import NewsletterForm from '@/Components/Marketing/NewsletterForm';
import Reveal from '@/Components/Marketing/Reveal';
import Section from '@/Components/Marketing/Section';
import SmartLink from '@/Components/Marketing/SmartLink';
import { BEHIND_CARDS_OPACITY, Cloud, FULL_WIDTH_ZONE } from '@/Components/Marketing/Texture';
import { GUIDE_COVER, GeneratedCover } from '@/Components/Marketing/Resources/Guides';
import { useGuides } from '@/Components/Marketing/Resources/data';
import { Heading, Lead } from '@/Components/Marketing/Typography';
import useNearViewport from '@/Components/Marketing/useNearViewport';
import { FOCUS_RING, RING_SURFACE } from '@/Components/Marketing/focusRing';
import { cn } from '@/lib/utils';

// Desktop: the newest article large over two of three columns and both rows,
// the next two stacked in the third (with covers, a 3:2 cover on the large
// card about matches the two 16:9 ones beside it). Tablet: the newest across
// the top, two below. Phone: one column.
const GRID = 'mt-12 grid grid-cols-1 gap-4 sm:mt-16 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2';
const FEATURED_SLOT = 'md:col-span-2 lg:row-span-2';

// The card does not clip (its cover has the card's top corners), so the
// title link's focus ring, drawn just outside it, shows whole.
const CARD = cn(
    'group relative flex h-full flex-col rounded-mk-card bg-mk-surface ring-1 ring-mk-line',
    'transition-shadow duration-500 ease-mk fine:hover:shadow-mk-soft motion-reduce:transition-none',
);

// Each card's cover across its top: the whole 16:9, and on the featured card
// 3:2 from 1024px, where it stands beside the two stacked ones. A hairline
// under it: a white cover would otherwise run into the card's white.
const COVER_FRAME = {
    featured: 'aspect-video rounded-t-mk-card border-b border-mk-line lg:aspect-[3/2]',
    compact: 'aspect-video rounded-t-mk-card border-b border-mk-line',
};

/**
 * The covers (ArticleCover and the cover template) are a chunk of their
 * own, out of Home's first files, asked for once the cards come within
 * PRELOAD_MARGIN of the viewport; until it is there, each frame holds its
 * place, empty.
 */
const loadCover = () => import('@/Components/Marketing/Cover/ArticleCover');

/**
 * Where the cloud behind the cards may show (Texture's Cloud, 2026-10-13):
 * the page's width, from the cards' top (under "All guides and articles",
 * whose ring needs the canvas for 3:1) into the section's foot, faded in and
 * out. Every control below that line sits on a card's own white.
 */
const CLOUD_ZONE = `-z-10 ${FULL_WIDTH_ZONE} top-0 -bottom-32 [mask-image:linear-gradient(to_bottom,transparent,#000_12%,#000_88%,transparent)]`;

/** The cloud in its zone: turned over, its heart past the cards' right edge, low, reaching into the section's foot. */
const CLOUD = 'bottom-[2rem] left-[calc(50%-4rem)] w-[44rem] -scale-x-100 sm:left-[calc(50%+16rem)] sm:w-[60rem]';

/**
 * Home's resources (2026-10-04, grown from its "Guides and articles"): the
 * three newest articles from the CMS, the guides to download, and the ways
 * to keep up: the compliance calendar, as a live piece of it (a battery
 * type's next obligations, CalendarPreview), and the regulation updates by
 * email, signed up for right in the card (2026-10-05; Home's footer leaves
 * out its own form). From 1024px the calendar stands in the right half
 * beside the guides and the sign-up. The articles
 * are fetched on mount (and again when the language changes); skeleton
 * cards of their shape hold the space meanwhile, and if the request fails
 * or comes back empty they step aside. The guides show once they arrive;
 * the rest is always there.
 */
export default function ResourcesHub() {
    const { locale, formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const headingId = useId();
    const [state, setState] = useState({ status: 'loading', articles: [] });
    const [listRef, near] = useNearViewport();
    const guides = useGuides(locale);

    useEffect(() => {
        let current = true;
        setState({ status: 'loading', articles: [] });

        axios
            .get(route('blog.articles.get', { page: 1, locale }))
            .then(res => {
                if (current) setState({ status: 'ready', articles: (res.data?.articles ?? []).slice(0, 3) });
            })
            .catch(() => {
                if (current) setState({ status: 'failed', articles: [] });
            });

        return () => {
            current = false;
        };
    }, [locale]);

    const downloads = guides.status === 'ready' ? guides.guides.filter((guide) => guide.content?.url).slice(0, 3) : [];

    return (
        <Section tone="light" aria-labelledby={headingId} className="relative isolate overflow-hidden border-t border-mk-line">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end lg:gap-16">
                <Reveal className="lg:col-span-8">
                    <Heading id={headingId}>{t('site.home.guides.title')}</Heading>
                    <Lead className="mt-6 max-w-[52ch]">{t('site.home.guides.lead')}</Lead>
                </Reveal>
                <Reveal delay={0.06} className="lg:col-span-4 lg:justify-self-end">
                    <Button href={route('outer.resources')} variant="secondary" size="lg">{t('site.home.guides.link')}</Button>
                </Reveal>
            </div>

            <div className="relative">
                <Cloud zone={CLOUD_ZONE} className={CLOUD} opacity={BEHIND_CARDS_OPACITY} />
                {state.status === 'loading' ? (
                    <Skeleton label={t('site.home.guides.loading')} />
                ) : (
                    state.articles.length > 0 && (
                        <ul ref={listRef} role="list" className={GRID}>
                            {state.articles.map((article, index) => (
                                <li key={article.id ?? article.slug} className={index === 0 ? FEATURED_SLOT : undefined}>
                                    <GuideCard article={article} featured={index === 0} withCover={near} />
                                </li>
                            ))}
                        </ul>
                    )
                )}

                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {downloads.length > 0 && (
                        <section data-downloads="" aria-labelledby={`${headingId}-downloads`} className="rounded-mk-card bg-mk-surface p-6 ring-1 ring-mk-line sm:p-8 lg:col-start-1 lg:row-start-1">
                            <h3 id={`${headingId}-downloads`} className="text-lg font-semibold tracking-[-0.02em] text-mk-ink">{t('site.resources.guides.title')}</h3>
                            <ul role="list" className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
                                {downloads.map((guide) => (
                                    <li key={guide.id ?? guide.slug}>
                                        <GuideDownload guide={guide} label={t('site.resources.guides.download')} />
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                    {/* Beside the guides and the sign-up, the calendar spans both their
                        rows, so the three cards end level; without guides, the sign-up
                        keeps its own height beside the calendar's top. */}
                    <CalendarCard
                        t={t}
                        id={`${headingId}-calendar`}
                        className={cn('lg:col-start-2 lg:row-start-1', downloads.length > 0 && 'lg:row-span-2')}
                    />
                    <NewsletterCard
                        t={t}
                        id={`${headingId}-newsletter`}
                        className={cn('lg:col-start-1', downloads.length > 0 ? 'lg:row-start-2' : 'lg:row-start-1 lg:self-start')}
                    />
                </div>
            </div>
        </Section>
    );
}

/** A guide to download: its A4 cover, small, its title and the download. */
function GuideDownload({ guide, label }) {
    const titleId = useId();

    return (
        <article data-guide={guide.slug} aria-labelledby={titleId} className="flex h-full gap-4 sm:flex-col">
            <div className="h-28 shrink-0 sm:h-36">
                {guide.featured_image ? (
                    <img
                        src={guide.featured_image}
                        alt=""
                        width={GUIDE_COVER.width}
                        height={GUIDE_COVER.height}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-auto rounded-sm bg-mk-surface object-contain shadow-mk-soft ring-1 ring-mk-ink/5"
                    />
                ) : (
                    <GeneratedCover title={guide.title} />
                )}
            </div>
            <div className="flex min-w-0 flex-col">
                <h4 id={titleId} className="text-[15px] font-semibold leading-snug text-balance text-mk-ink">{guide.title}</h4>
                <SmartLink
                    href={guide.content.url}
                    external
                    aria-describedby={titleId}
                    className={cn('mt-3 inline-flex w-fit items-center gap-1.5 rounded-sm text-sm font-medium text-mk-orange', FOCUS_RING)}
                >
                    <Download aria-hidden="true" strokeWidth={1.5} className="size-4" />
                    {label}
                </SmartLink>
            </div>
        </article>
    );
}

// A keep-up card's mark: the icon on a soft orange tile.
const MARK = 'inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-mk-orange-soft text-mk-orange';

/**
 * The regulation updates by email, signed up for here: the keep-up card's
 * head (its mark, title and line) over the shared sign-up form
 * (NewsletterForm, the footer's and the Resources page's), so a visitor
 * subscribes without leaving Home.
 */
function NewsletterCard({ t, id, className }) {
    return (
        <section
            data-keep-up="newsletter"
            aria-labelledby={id}
            className={cn('flex flex-col rounded-mk-card bg-mk-surface p-6 ring-1 ring-mk-line sm:p-8', RING_SURFACE.surface, className)}
        >
            <div className="flex gap-4">
                <span aria-hidden="true" className={MARK}>
                    <Mail strokeWidth={1.5} className="size-5" />
                </span>
                <div className="min-w-0">
                    <h3 id={id} className="text-lg font-semibold tracking-[-0.02em] text-mk-ink">{t('site.resources.newsletter.title')}</h3>
                    <p className="mt-1 text-[15px] leading-relaxed text-pretty text-mk-body">{t('site.footer.newsletter.text')}</p>
                </div>
            </div>
            {/* At the card's foot: beside the taller calendar the card stretches, and the form ends level with its link. */}
            <NewsletterForm className="mt-auto pt-6" />
        </section>
    );
}

/**
 * The compliance calendar, kept up with here and now: the keep-up card's
 * head (its mark, title and line), a battery type's next obligations
 * (CalendarPreview, bare inside this card) and the link to the calendar.
 * Its tabs and rows act on their own, so the card is no link.
 */
function CalendarCard({ t, id, className }) {
    return (
        <section
            data-keep-up="calendar"
            aria-labelledby={id}
            className={cn('flex flex-col rounded-mk-card bg-mk-surface p-6 ring-1 ring-mk-line sm:p-8', RING_SURFACE.surface, className)}
        >
            <div className="flex gap-4">
                <span aria-hidden="true" className={MARK}>
                    <CalendarDays strokeWidth={1.5} className="size-5" />
                </span>
                <div className="min-w-0">
                    <h3 id={id} className="text-lg font-semibold tracking-[-0.02em] text-mk-ink">{t('site.nav.calendar')}</h3>
                    <p className="mt-1 text-[15px] leading-relaxed text-pretty text-mk-body">{t('site.home.resources.calendar.text')}</p>
                </div>
            </div>
            <CalendarPreview framed={false} className="mt-6" />
            <ArrowLink href={route('outer.batterycompliancecalendar')} className="mt-6">
                {t('site.daterail.link')}
            </ArrowLink>
        </section>
    );
}

/**
 * One article: its cover (the article's own, from the cover template; its
 * empty frame until `withCover`, when the cards are near the viewport), its
 * date and category, then the title right under them (no gap held open
 * between the two, ask 7) and, on the featured card, three lines of its
 * description. The title is the link, stretched over the whole card, so the
 * card is one click target and one tab stop with the title as its name; no
 * corner arrow (audit 26). The cover is decoration (aria-hidden): the card
 * says all it shows. No reading time: the list the CMS answers carries no
 * article bodies to count.
 */
function GuideCard({ article, featured, withCover }) {
    const { formatDateToParts } = useIntl();
    const date = formatDay(formatDateToParts, article.publishedAt);
    const tag = article.tags?.[0]?.name;
    const frame = featured ? COVER_FRAME.featured : COVER_FRAME.compact;
    const placeholder = <div aria-hidden="true" data-cover-placeholder="" className={cn(frame, 'bg-mk-sunken')} />;

    return (
        <article className={CARD}>
            {withCover ? (
                <LazyBoundary load={loadCover} fallback={placeholder}>
                    {(ArticleCover) => <ArticleCover article={article} className={frame} zoom />}
                </LazyBoundary>
            ) : (
                placeholder
            )}
            <div className={featured ? 'p-8 sm:p-10' : 'p-6 sm:p-8'}>
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-mk-subtle">
                    {date && (
                        <time dateTime={article.publishedAt} className="tabular-nums">
                            {date}
                        </time>
                    )}
                    {date && tag && <span aria-hidden="true" className="size-1 rounded-full bg-mk-line" />}
                    {tag && <span data-guide-category="">{tag}</span>}
                </p>
                <div className={featured ? 'pt-6 sm:pt-8' : 'pt-4'}>
                    <h3
                        className={cn(
                            'font-semibold tracking-[-0.02em] text-balance text-mk-ink',
                            featured ? 'text-2xl leading-tight sm:text-3xl' : 'text-lg leading-snug',
                        )}
                    >
                        <SmartLink
                            href={route('blog.article.get', { slug: article.slug })}
                            className={cn(
                                'rounded-sm outline-none after:absolute after:inset-0 after:rounded-mk-card',
                                'focus-visible:after:ring-2 focus-visible:after:ring-mk-orange focus-visible:after:ring-offset-2 focus-visible:after:ring-offset-mk-canvas',
                            )}
                        >
                            {article.title}
                        </SmartLink>
                    </h3>
                    {featured && article.description && (
                        <p className="mt-4 line-clamp-3 max-w-[52ch] text-base leading-relaxed text-pretty text-mk-body">
                            {article.description}
                        </p>
                    )}
                </div>
            </div>
        </article>
    );
}

/** Placeholder cards in the loaded layout's exact shape. */
function Skeleton({ label }) {
    const bar = 'rounded bg-mk-ink/[0.06]';

    return (
        <div role="status" className={GRID}>
            <span className="sr-only">{label}</span>
            {[0, 1, 2].map(index => {
                const featured = index === 0;

                return (
                    <div
                        key={index}
                        aria-hidden="true"
                        data-skeleton=""
                        className={cn('flex flex-col overflow-hidden rounded-mk-card bg-mk-surface ring-1 ring-mk-line', featured && FEATURED_SLOT)}
                    >
                        <div className={cn(featured ? COVER_FRAME.featured : COVER_FRAME.compact, 'bg-mk-sunken')} />
                        <div className={featured ? 'p-8 sm:p-10' : 'p-6 sm:p-8'}>
                            <div className={cn(bar, 'h-4 w-28')} />
                            <div className={cn(bar, 'mt-5', featured ? 'h-8 w-4/5' : 'h-5 w-11/12')} />
                            <div className={cn(bar, 'mt-2', featured ? 'h-8 w-3/5' : 'h-5 w-2/3')} />
                            {featured && <div className={cn(bar, 'mt-6 h-4 w-full max-w-md')} />}
                            {featured && <div className={cn(bar, 'mt-2 h-4 w-5/6 max-w-sm')} />}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
